import { useCallback, useRef } from 'react';
import { useUnmount } from 'react-use';

import { ProviderPatientNoteConnectedProps } from '@grow-therapy-team/seedling-components';

import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum, ScribbleEditedParams } from '../../segment/types';

/**
 * Custom hook to record a Segment event for a provider editing their patient's
 * scribble (patient note).
 *
 * This hook is intended to tap into the auto-save functionality of the
 * `ProviderPatientNote` component to track edits made over the component's
 * lifecycle. When the component unmounts (either when the popover is dismissed
 * or when the client info drawer is closed), it fires a `SCRIBBLE_EDITED` event
 * with the relevant metadata about the edits. In this way, we only record a
 * single event for the whole, "logical" update after all of a provider's edits,
 * rather than recording multiple events each time the field is auto-saved.
 *
 * @param patientShortId The short ID of the patient whose scribble (patient
 * note) is being edited.
 * @param inSession Whether the provider is editing the scribble while
 * in-session with the patient.
 * @param appointmentShortId If `inSession` is true, the short ID of the
 * appointment during which the scribble is being edited, if one is found.
 * @returns An object containing the `onSaveNote` callback function, which
 * should be called with the new content of the scribble when it is saved, in
 * order to record metadata about the edits made.
 */
export function useRecordScribbleEdited<InSession extends boolean>(
  patientShortId: string,
  inSession: InSession,
  appointmentShortId: InSession extends true ? string : undefined,
): {
  onSaveNote: ProviderPatientNoteConnectedProps['onSaveNote'];
} {
  const { userInputted } = useTrackEvent();

  const editedContentMetadataRef = useRef<
    Pick<ScribbleEditedParams, 'editedLength' | 'paragraphCount'> & {
      hasEdited: boolean;
    }
  >({
    editedLength: 0,
    hasEdited: false,
    paragraphCount: 0,
  });

  const onSaveNote = useCallback(({ content }: { content: string }) => {
    editedContentMetadataRef.current.hasEdited = true;
    editedContentMetadataRef.current.editedLength = content.length;
    editedContentMetadataRef.current.paragraphCount = content.split(/\n+/).length;
  }, []);

  const recordScribbleEdited = useCallback(() => {
    if (!patientShortId || !editedContentMetadataRef.current.hasEdited) return;

    userInputted(EventTypeEnum.SCRIBBLE_EDITED, {
      patientShortId,
      appointmentShortId,
      inSession,
      editedLength: editedContentMetadataRef.current.editedLength,
      paragraphCount: editedContentMetadataRef.current.paragraphCount,
    });
  }, [patientShortId, userInputted, appointmentShortId, inSession]);

  useUnmount(recordScribbleEdited);

  return { onSaveNote };
}
